import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { landingAsset } from "../utils/landingAsset";
import "./LandingPages.scss";
import "./SolutionsLanding.scss";

const CARD_NETWORK = landingAsset("carrier-network.webp");
const CARD_DRIVER = landingAsset("driver-cab.webp");
const ROAD_IMG = landingAsset("carrier-network-road.webp");

const DotList = ({ items }) => (
  <div className="mtk-dot-list">
    {items.map((t) => (
      <div key={t} className="item">
        <span className="dot" />
        <span>{t}</span>
      </div>
    ))}
  </div>
);

const FeatIcon = ({ open }) => (
  <span className={`mtk-feat-icon${open ? " open" : ""}`} />
);

const FEATURES_LEFT = [
  { t: "Quotes management", d: "Send one request and compare all prices in one place." },
  { t: "Order management", d: "All your orders in one dashboard, with the information you need." },
  { t: "Complete delivery visibility", d: "Follow the progress of every shipment at each step." },
];

const FEATURES_RIGHT = [
  { t: "Invoice management", d: "Invoices tracked and reconciled with each delivery." },
  { t: "Simplified claims management", d: "Centralize claims and follow their resolution in one place." },
  { t: "eDocuments", d: "Order confirmations and proofs of delivery collected automatically." },
];

const MARQUEE = [
  "Work from a single platform",
  "Improve transport visibility",
  "Simplify carrier collaboration",
  "Work with your trusted carriers",
  "Standardize transport processes",
  "Simplify document management",
  "Reduce administrative workload",
];

const Solutions = () => {
  const [matchingOpen, setMatchingOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [featOpen, setFeatOpen] = useState(0);
  const connectRef = useRef(null);
  const onboardRef = useRef(null);

  // Preload the two above-the-fold card images so they paint ASAP
  useEffect(() => {
    const links = [CARD_NETWORK, CARD_DRIVER].map((href) => {
      const el = document.createElement("link");
      el.rel = "preload";
      el.as = "image";
      el.href = href;
      el.type = "image/webp";
      document.head.appendChild(el);
      return el;
    });
    return () => links.forEach((el) => el.remove());
  }, []);

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleFeat = (i) => setFeatOpen((cur) => (cur === i ? -1 : i));

  return (
    <div className="mtk-landing mtk-solutions-shipper">
      <section className="mtk-page-hero">
        <div className="hero-inner">
          <h1>
            Road freight,
            <br />
            <span>finally simple.</span>
          </h1>
        </div>
      </section>

      <section className="mtk-two-ways">
        <div className="mtk-wrap-1320">
          <h2>Two ways to connect with carriers.</h2>
          <p className="lead">
            Use our European carrier network, or bring your own partner carriers onto the platform.
          </p>

          <div className="cards">
            <div className="card">
              <div className="visual">
                <img
                  className="img"
                  src={CARD_NETWORK}
                  alt="Carriers matched on Mottruck"
                  width={900}
                  height={619}
                  decoding="async"
                  fetchPriority="high"
                />
                <span className="badge dark">Carriers matched</span>
              </div>
              <h3>Connect with our network</h3>
              <p>
                Give yourself more options by finding the carrier that suits you within our European
                carrier network.
              </p>
              <div className="link-row">
                <button type="button" className="linkish" onClick={() => scrollTo(connectRef)}>
                  Connect with carriers
                </button>
              </div>
            </div>

            <div className="card">
              <div className="visual end">
                <img
                  className="img"
                  src={CARD_DRIVER}
                  alt="Preferred carrier on Mottruck"
                  width={900}
                  height={619}
                  decoding="async"
                  fetchPriority="high"
                />
                <span className="badge light">Preferred carrier</span>
              </div>
              <h3>Onboard your own carriers</h3>
              <p>
                Bring the carriers you already work with onto the platform and run every shipment on
                the same process.
              </p>
              <div className="link-row">
                <button type="button" className="linkish" onClick={() => scrollTo(onboardRef)}>
                  Onboard your carriers
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mtk-connect" ref={connectRef}>
        <div className="mtk-wrap-1320">
          <h2>Connect with our carriers</h2>
          <p className="sec-lead">
            Shippers are instantly connected to a network of carriers available across Europe to
            take on their transport need.
          </p>

          <div className="connect-grid">
            <img
              className="road"
              src={ROAD_IMG}
              alt="Mottruck carrier network on the road"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
            />
            <div>
              <h3 className="block-title">More options for every shipment</h3>
              <div className="feat-rows">
                <div className="feat-row">
                  <div className="ico">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.6-3.6" />
                    </svg>
                  </div>
                  <h4>Matched on real availability</h4>
                  <p>
                    Carriers are matched to your real availability needs: loading place, capacity,
                    dates. No more sending requests to carriers one by one, no more endless
                    back-and-forth.
                  </p>
                </div>
                <div className="feat-row">
                  <div className="ico">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 3h11l3 3v15H5z" />
                      <path d="M9 9h6M9 13h6M9 17h4" />
                    </svg>
                  </div>
                  <h4>One request, several quotes</h4>
                  <p>
                    Send one request to multiple carriers and get all their quotes in one place.
                    Compare easily and pick the best fit, with no chasing and no delays.
                  </p>
                </div>
                <div className="feat-row">
                  <div className="ico">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l7 3v6c0 4.4-3 7.9-7 9-4-1.1-7-4.6-7-9V6z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </div>
                  <h4>Verified carriers</h4>
                  <p>
                    Every carrier is verified before joining the network, then rated after every
                    shipment, giving you full confidence before you book.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3 className="benefits-title">Benefits</h3>
          <div className="benefit-cards">
            {[
              ["Access more carriers", "Connect with carriers that match your needs and choose the shipping options that fit your business."],
              ["Best rates on the market", "Send one request to multiple carriers and compare quotes to secure competitive pricing."],
              ["Track your shipments easily", "Gain full visibility into your shipments through a dedicated tracking interface and automatic status updates, keeping you informed every step of the way."],
              ["Reduce communication time", "Access shipment information, documents, and updates in one place, eliminating unnecessary follow-ups and saving valuable time."],
            ].map(([t, d]) => (
              <div key={t} className="benefit-card">
                <h4>{t}</h4>
                <p>{d}</p>
              </div>
            ))}
          </div>

          <div className="expand-toggle-wrap">
            <button
              type="button"
              className="expand-toggle"
              onClick={() => setMatchingOpen((v) => !v)}
              aria-expanded={matchingOpen}
            >
              {matchingOpen ? "−" : "+"}
            </button>
          </div>

          {matchingOpen && (
            <div className="expand-matching">
              <div className="expand-top">
                <div>
                  <h3>
                    <span className="h-ico">
                      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#1A244D" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="14" r="7" />
                        <circle cx="11" cy="14" r="3" />
                        <circle cx="11" cy="14" r="0.9" fill="#1A244D" />
                        <path d="m11 14 8-8" />
                        <path d="M15.5 6.5 19 6l-.5 3.5" />
                      </svg>
                    </span>
                    Made for Shippers who…
                  </h3>
                  <DotList
                    items={[
                      "Need quick access to carriers that match their transport requirements",
                      "Want to move away from manual processes and simplify transport management",
                      "Need better visibility across their transport operations",
                      "Want competitive rates and reliable capacity",
                      "Want to compare multiple quotes before making a decision",
                    ]}
                  />
                </div>
                <div>
                  <h3>
                    <span className="h-ico">
                      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#D64545" strokeWidth="2.4" strokeLinecap="round">
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </span>
                    What it removes
                  </h3>
                  <DotList
                    items={[
                      "Carrier dependency and limited transport options",
                      "Scattered transport information across multiple channels",
                      "Manual follow-ups and unnecessary back-and-forth",
                      "Time-consuming coordination",
                      "The risk of overpaying due to a lack of market comparison",
                    ]}
                  />
                </div>
              </div>

              <h3 className="how-title">
                <span className="h-ico">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#1A244D" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
                  </svg>
                </span>
                How it works
              </h3>
              <div className="how-steps">
                <div className="line" />
                {[
                  ["Enter shipment details", "Provide your shipment information and transport requirements."],
                  ["Get matched", "Matching carriers appear based on your requirements."],
                  ["Compare quotes", "View and compare all quotes in one place."],
                  ["Choose your carrier", "Select the carrier that best fits your requirements."],
                  ["Track and collect", "Monitor your shipment and access transport documents in one place."],
                ].map(([t, d], i) => (
                  <div key={t} className="step">
                    <span className="num">{i + 1}</span>
                    <h4>{t}</h4>
                    <p>{d}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mtk-onboard" ref={onboardRef}>
        <div className="mtk-wrap-1320">
          <h2>Onboard your carriers</h2>
          <p className="sec-lead">
            This option allows shippers to integrate their regular carriers within the platform and
            work only with them.
          </p>

          <div className="onboard-grid">
            <div className="left-col">
              <h3 className="with-ico">
                <span className="h-ico">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#1A244D" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18" />
                    <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
                    <circle cx="12" cy="3" r="1.6" fill="#1A244D" />
                    <circle cx="4.5" cy="16.5" r="1.6" fill="#1A244D" />
                    <circle cx="19.5" cy="16.5" r="1.6" fill="#1A244D" />
                  </svg>
                </span>
                <span>Connect your carriers to your digital ecosystem</span>
              </h3>
              <div
                className="pref-img"
                style={{ backgroundImage: `url(${landingAsset("preferred-carriers.png")})` }}
              />
            </div>

            <div>
              <h3 className="block-title">A simplified collaboration</h3>
              <div className="feat-rows">
                <div className="feat-row">
                  <div className="ico">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 7h11v9H3z" />
                      <path d="M14 10h4l3 3v3h-7z" />
                      <circle cx="7" cy="18" r="1.7" />
                      <circle cx="17.5" cy="18" r="1.7" />
                    </svg>
                  </div>
                  <h4>Instantly connect your carriers</h4>
                  <p>
                    Bring your existing carriers onto the platform in minutes. No renegotiation, no
                    disruption — just your regular partners, now working from a shared digital space.
                  </p>
                </div>
                <div className="feat-row">
                  <div className="ico">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                  </div>
                  <h4>One process, every stakeholder</h4>
                  <p>
                    Quoting, orders, tracking and documents follow the same standardized workflow for
                    every carrier — so nothing gets lost between emails, calls and spreadsheets.
                  </p>
                </div>
                <div className="feat-row">
                  <div className="ico">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
                      <circle cx="12" cy="12" r="2.6" />
                    </svg>
                  </div>
                  <h4>Full visibility, zero blind spots</h4>
                  <p>
                    Every shipment status is visible to your whole team in real time, so you can
                    anticipate issues and keep your customers informed without extra back-and-forth.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="marquee-label">Benefits</div>
          <div className="marquee">
            <div className="marquee-track">
              {[...MARQUEE, ...MARQUEE].map((t, i) => (
                <span key={`${t}-${i}`}>{t}</span>
              ))}
            </div>
          </div>

          <div className="expand-toggle-wrap">
            <button
              type="button"
              className="expand-toggle"
              onClick={() => setOnboardingOpen((v) => !v)}
              aria-expanded={onboardingOpen}
            >
              {onboardingOpen ? "−" : "+"}
            </button>
          </div>

          {onboardingOpen && (
            <div className="expand-onboard">
              <div>
                <h3>
                  <span className="h-ico">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#1A244D" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="14" r="7" />
                      <circle cx="11" cy="14" r="3" />
                      <circle cx="11" cy="14" r="0.9" fill="#1A244D" />
                      <path d="m11 14 8-8" />
                      <path d="M15.5 6.5 19 6l-.5 3.5" />
                    </svg>
                  </span>
                  Made for Shippers who…
                </h3>
                <DotList
                  items={[
                    "Want to bring all their carriers onto one platform",
                    "Work with trusted carriers they want to keep",
                    "Want greater visibility across shipments",
                    "Need an easier way to onboard carrier partners",
                    "Want more consistent transport processes",
                  ]}
                />
              </div>
              <div>
                <h3>
                  <span className="h-ico">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#1A244D" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
                    </svg>
                  </span>
                  How it works
                </h3>
                <div className="timeline">
                  <div className="vline" />
                  {[
                    ["Invite your carriers", "Let your carriers know you work on the platform and invite them to create their account."],
                    ["Account set-up", "Once their account is created, you link it to yours in your preferred carriers list."],
                    ["Work only with your panel", "Your transport requests go only to these carriers."],
                    ["Work the same way", "Quoting, orders, tracking, claims and documents follow one standardized process."],
                  ].map(([t, d], i) => (
                    <div key={t} className="tl-step">
                      <span className="num">{i + 1}</span>
                      <h4>{t}</h4>
                      <p>{d}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3>
                  <span className="h-ico">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#D64545" strokeWidth="2.4" strokeLinecap="round">
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </span>
                  What it removes
                </h3>
                <DotList
                  items={[
                    "Scattered exchanges across emails, calls and spreadsheets",
                    "A different working process for every carrier",
                    "Manual follow-ups to know where a shipment stands",
                    "Transport documents arriving late or getting lost",
                    "Carriers working outside your transport management system",
                  ]}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mtk-features-included">
        <div className="inner">
          <div className="label">Features included</div>
          <div className="rows">
            <div className="row left-feats">
              <div className="feat-col">
                {FEATURES_LEFT.map((f, i) => (
                  <button
                    key={f.t}
                    type="button"
                    className="feat-acc"
                    onClick={() => toggleFeat(i)}
                  >
                    <div className="top">
                      <FeatIcon open={featOpen === i} />
                      <h4>{f.t}</h4>
                    </div>
                    {featOpen === i && <p>{f.d}</p>}
                  </button>
                ))}
              </div>
              <div className="mock">
                <img src={landingAsset("quotes-mockup.png")} alt="Quotes management" />
              </div>
            </div>

            <div className="row right-feats">
              <div className="mock">
                <img src={landingAsset("claims-mockup.png")} alt="Claims management" />
              </div>
              <div className="feat-col">
                {FEATURES_RIGHT.map((f, i) => (
                  <button
                    key={f.t}
                    type="button"
                    className="feat-acc"
                    onClick={() => toggleFeat(i + 3)}
                  >
                    <div className="top">
                      <FeatIcon open={featOpen === i + 3} />
                      <h4>{f.t}</h4>
                    </div>
                    {featOpen === i + 3 && <p>{f.d}</p>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mtk-explore-cta">
        <h2>Explore the platform.</h2>
        <Link to="/demo" className="cta">
          Book a demo →
        </Link>
      </section>
    </div>
  );
};

export default Solutions;
